'use client';

/**
 * Says what happened when an email link opens without signing anyone in.
 *
 * Sign-up confirmations and email sign-in links use the PKCE flow: the link
 * confirms the address, then signs the person in only in the browser where
 * they asked for it, which holds the code verifier. Opened anywhere else (a
 * phone's mail app, a work computer) the address is confirmed but nobody is
 * signed in, and the page used to show nothing about it. From July to
 * October 2026, 3 of the 15 patients who confirmed by email never signed in.
 * A link that has expired or was already used comes back with an error
 * instead, which got no explanation either.
 *
 * Renders nothing unless the URL carries a Supabase auth code (a UUID) or an
 * auth error, and no session came of it.
 */

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { trackEvent } from '@/lib/analytics';

type Kind = 'confirmed_elsewhere' | 'expired';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const AUTH_KEYS = ['code', 'error', 'error_code', 'error_description'];
// These pages handle their own links.
const OWN_HANDLING = ['/auth/callback', '/auth/confirm', '/api/auth', '/reset-password'];

const COPY: Record<Kind, { title: string; body: string }> = {
  confirmed_elsewhere: {
    title: 'Your email is confirmed',
    body: 'The link signs you in only in the browser where you created your account. Sign in here to continue.',
  },
  expired: {
    title: 'That link has expired or was already used',
    body: 'If you have already confirmed your email, sign in. If not, use the newest email we sent you.',
  },
};

export function AuthLinkNotice() {
  const [kind, setKind] = useState<Kind | null>(null);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (OWN_HANDLING.some((p) => url.pathname.startsWith(p))) return;
    const hash = new URLSearchParams(url.hash.startsWith('#') ? url.hash.slice(1) : '');
    const hasError = ['error', 'error_code'].some((k) => url.searchParams.has(k) || hash.has(k));
    const code = url.searchParams.get('code') ?? '';
    if (!hasError && !UUID.test(code)) return;

    let cancelled = false;
    // getSession waits for the client to finish its own attempt with the URL.
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled || data.session) return;
      const found: Kind = hasError ? 'expired' : 'confirmed_elsewhere';
      setKind(found);
      trackEvent('auth_link_notice', { kind: found });
      // Tidy the URL so a reload does not try the link again.
      for (const k of AUTH_KEYS) url.searchParams.delete(k);
      const keepHash = AUTH_KEYS.some((k) => hash.has(k)) ? '' : url.hash;
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${keepHash}`);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!kind) return null;
  const copy = COPY[kind];
  return (
    <div
      role="status"
      className="fixed inset-x-4 bottom-4 z-50 mx-auto max-w-md rounded-xl border border-border bg-card p-4 shadow-lg sm:inset-x-auto sm:left-1/2 sm:w-[28rem] sm:-translate-x-1/2"
    >
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <p className="font-semibold text-foreground">{copy.title}</p>
          <p className="mt-1 text-sm text-muted-foreground">{copy.body}</p>
          <Link
            href="/login"
            className="mt-3 inline-flex items-center rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
          >
            Sign in
          </Link>
        </div>
        <button
          type="button"
          onClick={() => setKind(null)}
          aria-label="Dismiss"
          className="rounded-md p-1 text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default AuthLinkNotice;
